"use client";

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "@/lib/api/notifications";
import { API_CONFIG } from "@/lib/config/api.config";
import { logger } from "@/utils/logger";
import { notificationKeys } from "./useNotifications";
import { useAuth } from "./useAuth";

const BASE_BACKOFF_MS = 2_000;
const MAX_BACKOFF_MS = 30_000;

/**
 * Opens the live notification SSE stream while the user is authenticated and
 * invalidates the notification queries whenever the server nudges.
 *
 * The stream is only a "something changed, refetch" trigger — never the source
 * of truth. Mount this once, high in the authenticated tree.
 *
 * Two things this codebase forces us to do differently from a plain EventSource:
 *   1. The ticket (`POST /notifications/stream-ticket`) goes through the normal
 *      API proxy; the stream connection itself connects DIRECTLY to the backend
 *      (`API_CONFIG.BASE_URL`), bypassing the proxy — the proxy buffers responses
 *      and runs as a time-limited serverless function, both fatal for SSE.
 *   2. Tickets are single-use and expire in 30s, so native EventSource
 *      reconnection (which reuses the same URL/ticket) always fails. On `error`
 *      we close, mint a fresh ticket, and reconnect with backoff.
 */
export function useNotificationStream() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof window === "undefined" || !("EventSource" in window)) return;

    let es: EventSource | null = null;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      const delay = Math.min(
        BASE_BACKOFF_MS * 2 ** attempt,
        MAX_BACKOFF_MS
      );
      attempt += 1;
      retryTimer = setTimeout(connect, delay);
    };

    async function connect() {
      if (cancelled) return;

      try {
        const res = await notificationsApi.getStreamTicket();
        const ticket = res.data?.data?.ticket;
        if (cancelled || !ticket) {
          scheduleReconnect();
          return;
        }

        es = new EventSource(
          `${API_CONFIG.BASE_URL}/notifications/stream?ticket=${encodeURIComponent(
            ticket
          )}`
        );

        es.onopen = () => {
          attempt = 0;
          logger.log("[notifications] stream connected");
        };

        es.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (
              payload?.event === "created" ||
              payload?.event === "read" ||
              payload?.event === "read_all"
            ) {
              invalidate();
            }
          } catch (error) {
            logger.error("[notifications] malformed stream payload:", error);
          }
        };

        // Named keep-alive event (every ~20s). Content is always `{}` — ignore it.
        es.addEventListener("heartbeat", () => {});

        es.onerror = () => {
          es?.close();
          es = null;
          scheduleReconnect();
        };
      } catch (error) {
        logger.error("[notifications] failed to open stream:", error);
        scheduleReconnect();
      }
    }

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      es?.close();
      es = null;
    };
  }, [isAuthenticated, queryClient]);
}
